import { useEffect, useMemo, useState } from "react"
import { default as ModalProvider } from "react-modal"
import { useNavigate } from "react-router-dom"
import type { DurationCost } from "../../context/credits"

interface PromoteDurationModalProps {
    isOpen: boolean
    title: string
    subtitle?: string
    /** Treptele de durată cu costul în credite (din backend, niciodată hardcodate). */
    tiers: DurationCost[]
    /** Soldul curent, pentru a avertiza din timp dacă nu ajunge. */
    balance: number | null
    /** Ziua recomandată (badge). Implicit treapta din mijloc. */
    recommendedDays?: number
    loading?: boolean
    confirmLabel?: string
    onConfirm: (durationDays: number) => void
    onCancel: () => void
}

const PromoteDurationModal = ({
    isOpen,
    title,
    subtitle,
    tiers,
    balance,
    recommendedDays,
    loading = false,
    confirmLabel = "Promovează",
    onConfirm,
    onCancel,
}: PromoteDurationModalProps) => {
    const navigate = useNavigate()
    const recommended = recommendedDays ?? (tiers.length ? tiers[Math.floor(tiers.length / 2)].days : undefined)
    const [days, setDays] = useState<number | null>(null)

    // La deschidere / schimbarea treptelor, preselectează recomandarea.
    useEffect(() => {
        if (isOpen && tiers.length) {
            setDays((prev) => (prev != null && tiers.some((t) => t.days === prev) ? prev : recommended ?? tiers[0].days))
        }
    }, [isOpen, tiers, recommended])

    const selected = useMemo(() => tiers.find((t) => t.days === days) ?? null, [tiers, days])
    const cost = selected?.cost ?? 0
    const notEnough = balance != null && cost > 0 && balance < cost

    return (
        <ModalProvider
            appElement={document.getElementById("root")!}
            isOpen={isOpen}
            onRequestClose={() => { if (!loading) onCancel() }}
            className="mx-4 my-auto w-full max-w-[440px] rounded-2xl bg-white outline-none"
            overlayClassName="fixed inset-0 z-[60] flex items-center justify-center bg-gray-800/60 p-4"
        >
            <div className="p-6 sm:p-7">
                <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-figmaPrimary/10 text-figmaPrimary">
                    <svg viewBox="0 0 24 24" fill="currentColor" className="h-7 w-7">
                        <path d="M12 2l2.9 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l7.1-1.01L12 2z" />
                    </svg>
                </div>

                <h3 className="mt-4 text-center text-lg font-bold text-figmaBlack">{title}</h3>
                {subtitle && (
                    <p className="mt-2 text-center text-sm text-figmaDarkGray leading-relaxed">{subtitle}</p>
                )}

                {tiers.length === 0 ? (
                    <p className="mt-5 text-center text-sm text-figmaDarkGray py-4">Se încarcă tarifele…</p>
                ) : (
                    <div className="mt-5 grid grid-cols-3 gap-3">
                        {tiers.map((t) => (
                            <button
                                key={t.days}
                                type="button"
                                onClick={() => setDays(t.days)}
                                className={`relative rounded-xl border-2 p-3 text-center transition-colors ${
                                    days === t.days
                                        ? "border-figmaPrimary bg-figmaPrimary/5"
                                        : "border-figmaLightGray hover:border-figmaPrimary/50"
                                }`}
                            >
                                {t.days === recommended && (
                                    <span className="absolute -top-2 left-1/2 -translate-x-1/2 rounded-full bg-figmaPrimary px-2 py-0.5 text-[10px] font-bold text-white whitespace-nowrap">
                                        Recomandat
                                    </span>
                                )}
                                <p className="text-sm font-bold text-figmaBlack">{t.days} zile</p>
                                <p className="mt-1 text-xs font-semibold text-figmaPrimary">
                                    {t.cost === 0 ? "Gratuit" : `${t.cost} credite`}
                                </p>
                            </button>
                        ))}
                    </div>
                )}

                {balance != null && tiers.length > 0 && (
                    <div className={`mt-4 rounded-xl px-4 py-3 text-sm border ${
                        notEnough ? "bg-red-50 border-red-200" : "bg-figmaLightGray border-figmaLightGray"
                    }`}>
                        {notEnough ? (
                            <>
                                <p className="text-figmaBlack">
                                    Ai nevoie de <span className="font-bold">{cost} credite</span> · sold curent: {balance}
                                </p>
                                <button
                                    type="button"
                                    onClick={() => navigate("/credits")}
                                    className="mt-1 font-bold text-figmaContrast underline underline-offset-2"
                                >
                                    Cumpără un pachet de credite
                                </button>
                            </>
                        ) : (
                            <p className="text-figmaBlack">
                                Cost: <span className="font-bold">{cost} credite</span>
                                <span className="text-figmaDarkGray"> · sold curent: {balance}</span>
                            </p>
                        )}
                    </div>
                )}

                <div className="mt-6 flex gap-3">
                    <button
                        type="button"
                        onClick={onCancel}
                        disabled={loading}
                        className="flex-1 rounded-xl bg-figmaLightGray px-4 py-3 text-sm font-semibold text-figmaBlack transition-colors hover:bg-figmaDarkBg/30 disabled:opacity-50"
                    >
                        Anulează
                    </button>
                    <button
                        type="button"
                        onClick={() => days != null && onConfirm(days)}
                        disabled={loading || days == null || notEnough}
                        className="flex flex-[1.4] items-center justify-center gap-2 rounded-xl bg-figmaPrimary px-4 py-3 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-50"
                    >
                        {loading ? (
                            <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                        ) : (
                            cost > 0 ? `${confirmLabel} — ${cost} credite` : confirmLabel
                        )}
                    </button>
                </div>
            </div>
        </ModalProvider>
    )
}

export default PromoteDurationModal
